import { supabase, forceSessionRefresh, clearAuthState, prepareAuthFlow, generateCodeVerifier } from './supabase';
import { getUserSubscription, getUserOrders } from './stripe';

// Define types for authentication
export type AuthProvider = 'google' | 'email' | 'phone';
export type AuthResult = {
  success: boolean;
  user: any | null;
  error?: string;
  isNewUser?: boolean;
  redirectTo?: string;
  url?: string;
};

// List of allowed email domains (if applicable)
const ALLOWED_EMAIL_DOMAINS = ['gmail.com', 'test.at', 'gmx.at'];
// List of test users that are always allowed
const ALLOWED_TEST_EMAILS = ['testhuber@gmail.com', 'ronaldm@test.at', 'elli@test.at', 'technogurke44@gmail.com', 'silviad@test.at', 'kathi@test.at', 'karina@test.at', 'allgemein@test.at', 'floa@test.at', 'basscharlie@test.at', 'trommelcharly@test.at', 'chrissys@test.at', 'stefank@test.at', 'alinad@test.at', 'karints@test.at', 'michaelts@test.at', 'eliasts@test.at', 'jessya@test.at', 'klaraa@test.at', 'kenanue@test.at', 'markuso@test.at', 'jaquiw@test.at', 'angieb@test.at'];

/**
 * Initiates OAuth2 authentication with Google
 * @returns Promise with redirect URL
 */
export const signInWithGoogle = async (): Promise<{ url: string | null; error?: string }> => {
  try {
    // Clear any existing auth state
    await clearAuthState();
    
    // Prepare new auth flow
    await prepareAuthFlow();
    
    // Generate and store a code verifier for PKCE
    generateCodeVerifier();
    
    // Get the current origin for the redirect URL
    const redirectUrl = `${window.location.origin}/auth/callback`;
    
    console.log("Starting Google login with redirect URL:", redirectUrl);
    
    const { data, error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: redirectUrl,
        queryParams: {
          // Pass additional parameters to be saved with the user
          access_type: 'offline',
          prompt: 'consent'
        }
      }
    });
    
    if (error) {
      console.error("Google login error:", error);
      return { url: null, error: error.message };
    }
    
    if (!data.url) {
      return { url: null, error: 'No redirect URL returned from authentication provider' };
    }
    
    return { url: data.url };
  } catch (error: any) {
    console.error("Unexpected Google login error:", error);
    return { url: null, error: error.message };
  }
};

/**
 * Handles the OAuth callback and processes user authentication
 * @param url The full callback URL with auth parameters
 * @returns AuthResult object with authentication status
 */
export const handleAuthCallback = async (url: string): Promise<AuthResult> => {
  try {
    console.log("Processing authentication callback");
    
    // Clear any existing auth state to prevent conflicts
    await clearAuthState();
    
    // Prepare new auth flow
    await prepareAuthFlow();
    
    // Generate a new code verifier for PKCE
    generateCodeVerifier();
    
    // Parse the URL to extract code or token
    const parsedUrl = new URL(url);
    let code = parsedUrl.searchParams.get('code');
    let token = parsedUrl.searchParams.get('token');
    let type = parsedUrl.searchParams.get('type');
    
    // If no code/token in URL params, check hash fragment
    if (!code && !token && parsedUrl.hash) {
      const hashParams = new URLSearchParams(parsedUrl.hash.substring(1));
      code = hashParams.get('code');
      token = hashParams.get('token');
      type = hashParams.get('type');
    }
    
    if (!code && !token) {
      console.error("No authentication code or token found");
      return { 
        success: false, 
        user: null, 
        error: "No authentication code or token found" 
      };
    }
    
    // Process based on auth type
    if (type === 'recovery') {
      // Password reset flow
      return { 
        success: true, 
        user: null, 
        redirectTo: '/update-password' 
      };
    } else {
      // Normal authentication flow (signup, signin, etc.)
      let authResult;
      
      if (token) {
        // Email confirmation or magic link
        authResult = await supabase.auth.verifyOtp({
          token_hash: token,
          type: type === 'signup' ? 'signup' : 'email',
        });
      } else if (code) {
        // OAuth (like Google)
        authResult = await supabase.auth.exchangeCodeForSession(code);
      } else {
        return { 
          success: false, 
          user: null, 
          error: "Invalid authentication parameters" 
        };
      }
      
      if (authResult.error) {
        console.error("Authentication error:", authResult.error);
        return { 
          success: false, 
          user: null, 
          error: authResult.error.message 
        };
      }
      
      if (!authResult.data.session) {
        return { 
          success: false, 
          user: null, 
          error: "Failed to create session" 
        };
      }
      
      const user = authResult.data.user;
      
      // Check if email domain is allowed (if applicable)
      if (user.email && !isEmailAllowed(user.email)) {
        // Log the unauthorized attempt
        logAuthEvent({
          userId: user.id,
          email: user.email,
          action: 'domain_restricted',
          success: false,
          details: `Email domain not allowed: ${user.email.split('@')[1]}`
        });
        
        // Sign out the user
        await supabase.auth.signOut();
        
        return {
          success: false,
          user: null,
          error: "Your email domain is not authorized to use this application"
        };
      }
      
      // Determine if this is a new user
      const isNewUser = user.app_metadata.provider === 'google' && 
                        user.app_metadata.providers?.length === 1 &&
                        user.created_at === user.updated_at;
      
      // Force a session refresh to ensure we have the latest data
      await forceSessionRefresh();
      
      // Check if user has premium status
      let isPremium = false;
      try {
        const subscription = await getUserSubscription();
        const orders = await getUserOrders();
        
        // Check if user has active subscription or lifetime purchase
        isPremium = subscription?.status === 'active' || 
                   orders?.some(order => order.payment_status === 'paid');
      } catch (error) {
        console.warn("Error checking premium status:", error);
        // Continue with non-premium status
      }
      
      // Log the successful authentication
      logAuthEvent({
        userId: user.id,
        email: user.email,
        action: isNewUser ? 'signup' : 'login',
        success: true,
        provider: user.app_metadata.provider,
        isPremium
      });
      
      return {
        success: true,
        user,
        isNewUser,
        redirectTo: isNewUser ? '/profile-settings' : '/'
      };
    }
  } catch (error: any) {
    console.error("Error handling auth callback:", error);
    
    // Log the failed authentication attempt
    logAuthEvent({
      userId: null,
      email: null,
      action: 'callback_error',
      success: false,
      details: error.message
    });
    
    return {
      success: false,
      user: null,
      error: error.message
    };
  }
};

/**
 * Checks if the email domain is allowed
 * @param email User's email address
 * @returns boolean indicating if email is allowed
 */
const isEmailAllowed = (email: string): boolean => {
  // Always allow test users
  if (ALLOWED_TEST_EMAILS.includes(email)) {
    return true;
  }
  
  // If no domain restrictions, allow all emails
  if (ALLOWED_EMAIL_DOMAINS.length === 0) {
    return true;
  }
  
  // Check domain against allowed list
  const domain = email.split('@')[1];
  return ALLOWED_EMAIL_DOMAINS.includes(domain);
};

/**
 * Logs authentication events for security monitoring
 * @param event The authentication event to log
 */
const logAuthEvent = async (event: {
  userId: string | null;
  email: string | null;
  action: string;
  success: boolean;
  provider?: string;
  details?: string;
  isPremium?: boolean;
}): Promise<void> => {
  try {
    // Log to console for development
    console.log('Auth event:', event);
    
    // In production, you would send this to your logging service
    // For now, we'll just log to Supabase if we have a valid session
    const { data: { session } } = await supabase.auth.getSession();
    
    if (session) {
      // Log to a hypothetical auth_logs table (you would need to create this)
      // This is just an example - implement according to your needs
      /*
      await supabase
        .from('auth_logs')
        .insert({
          user_id: event.userId,
          email: event.email,
          action: event.action,
          success: event.success,
          provider: event.provider,
          details: event.details,
          ip_address: null, // Would need to be captured server-side
          user_agent: navigator.userAgent,
          timestamp: new Date()
        });
      */
    }
  } catch (error) {
    console.error('Error logging auth event:', error);
    // Non-critical error, don't throw
  }
};